import React, { useState, useEffect, useCallback } from 'react';
import { 
  Users, Building2, Briefcase, RefreshCw, Search, Plus, 
  ChevronRight, Mail, Phone, Calendar, DollarSign, 
  ExternalLink, AlertCircle, CheckCircle2, X, User,
  Building, TrendingUp, Clock, ArrowUpRight, FolderPlus,
  Folder, Check, ChevronDown, ChevronLeft, Loader2,
  LayoutGrid, LayoutList, Globe
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API_BASE = `${window.location.origin}/api`;

// Company Logo component with fallback to initials
const CompanyLogo = ({ logoUrl, domain, name, initials, size = 'md', className = '' }) => {
  const [showFallback, setShowFallback] = useState(!logoUrl);

  const sizeClasses = {
    sm: 'w-10 h-10',
    md: 'w-14 h-14',
    lg: 'w-16 h-16',
  };

  const textSizes = {
    sm: 'text-sm',
    md: 'text-lg',
    lg: 'text-xl',
  };

  const handleError = () => {
    setShowFallback(true);
  };

  return (
    <div className={`${sizeClasses[size]} rounded-xl overflow-hidden bg-white/10 flex items-center justify-center ${className}`}>
      {!showFallback && logoUrl ? (
        <img 
          src={logoUrl}
          alt={name || 'Company'}
          className="w-full h-full object-contain p-1"
          onError={handleError}
        />
      ) : (
        <span className={`text-orange-400 font-bold ${textSizes[size]}`}>
          {initials || (name?.[0] || '?').toUpperCase()}
        </span>
      )}
    </div>
  );
};

// Format currency
const formatCurrency = (amount) => {
  if (!amount) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(parseFloat(amount));
};

// Format date
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

// Format date with time
const formatDateTime = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

// Deal stage colors
const getStageColor = (stage) => {
  const colors = {
    'appointmentscheduled': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    'qualifiedtobuy': 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    'presentationscheduled': 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
    'decisionmakerboughtin': 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    'contractsent': 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    'closedwon': 'bg-green-500/20 text-green-400 border-green-500/30',
    'closedlost': 'bg-red-500/20 text-red-400 border-red-500/30',
  };
  return colors[stage?.toLowerCase()] || 'bg-gray-500/20 text-gray-400 border-gray-500/30';
};

// Format stage name
const formatStageName = (stage) => {
  if (!stage) return 'Unknown';
  return stage
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase())
    .trim();
};

// Lifecycle stage badge
const LifecycleBadge = ({ stage }) => {
  const colors = {
    'subscriber': 'bg-gray-500/20 text-gray-400',
    'lead': 'bg-blue-500/20 text-blue-400',
    'marketingqualifiedlead': 'bg-purple-500/20 text-purple-400',
    'salesqualifiedlead': 'bg-cyan-500/20 text-cyan-400',
    'opportunity': 'bg-yellow-500/20 text-yellow-400',
    'customer': 'bg-green-500/20 text-green-400',
    'evangelist': 'bg-pink-500/20 text-pink-400',
  };
  const color = colors[stage?.toLowerCase()] || 'bg-gray-500/20 text-gray-400';
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${color}`}>
      {stage || 'Unknown'}
    </span>
  );
};

export default function HubSpotPage() {
  const [activeTab, setActiveTab] = useState('contacts');
  const [isConnected, setIsConnected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  // View mode states
  const [contactsViewMode, setContactsViewMode] = useState('list'); // 'list' or 'grid'
  const [companiesViewMode, setCompaniesViewMode] = useState('list'); // 'list' or 'grid'
  const [dealsViewMode, setDealsViewMode] = useState('list'); // 'list' or 'grid'
  
  // Filter states for Contacts
  const [lifecycleFilter, setLifecycleFilter] = useState('');
  const [sortBy, setSortBy] = useState('lastmodifieddate');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // Filter states for Companies
  const [companySortBy, setCompanySortBy] = useState('name');
  const [companySortOrder, setCompanySortOrder] = useState('asc');
  const [industryFilter, setIndustryFilter] = useState('');
  const [companySearchQuery, setCompanySearchQuery] = useState('');
  
  // Filter states for Deals
  const [dealStageFilter, setDealStageFilter] = useState('');
  const [dealSortBy, setDealSortBy] = useState('createdate');
  const [dealSortOrder, setDealSortOrder] = useState('desc');
  const [selectedDeals, setSelectedDeals] = useState(new Set());
  const [dealSearchQuery, setDealSearchQuery] = useState('');
  
  // Data states
  const [contacts, setContacts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [deals, setDeals] = useState([]);
  const [pipelines, setPipelines] = useState([]);
  const [owners, setOwners] = useState([]);
  
  // Pagination states
  const [contactsPaging, setContactsPaging] = useState(null);
  const [companiesPaging, setCompaniesPaging] = useState(null);
  const [dealsPaging, setDealsPaging] = useState(null);
  
  // Selection states
  const [selectedContacts, setSelectedContacts] = useState(new Set());
  const [selectedCompanies, setSelectedCompanies] = useState(new Set());
  
  // Folder states
  const [folders, setFolders] = useState([]);
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [showCreateFolder, setShowCreateFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  
  // Modal states
  const [selectedContact, setSelectedContact] = useState(null);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [showCreateContact, setShowCreateContact] = useState(false);
  const [showCreateDeal, setShowCreateDeal] = useState(false);
  
  // Stats
  const [stats, setStats] = useState({
    totalContacts: 0,
    totalCompanies: 0,
    totalDeals: 0,
    totalPipeline: 0,
  });

  // Lifecycle stages for filter
  const lifecycleStages = [
    { value: '', label: 'All Stages' },
    { value: 'subscriber', label: 'Subscriber' },
    { value: 'lead', label: 'Lead' },
    { value: 'marketingqualifiedlead', label: 'Marketing Qualified Lead' },
    { value: 'salesqualifiedlead', label: 'Sales Qualified Lead' },
    { value: 'opportunity', label: 'Opportunity' },
    { value: 'customer', label: 'Customer' },
    { value: 'evangelist', label: 'Evangelist' },
  ];

  // Sort options
  const sortOptions = [
    { value: 'lastmodifieddate_desc', label: 'Last Activity (Newest)' },
    { value: 'lastmodifieddate_asc', label: 'Last Activity (Oldest)' },
    { value: 'createdate_desc', label: 'Date Added (Newest)' },
    { value: 'createdate_asc', label: 'Date Added (Oldest)' },
    { value: 'firstname_asc', label: 'Name (A-Z)' },
    { value: 'firstname_desc', label: 'Name (Z-A)' },
  ];

  // Sort options for Companies
  const companySortOptions = [
    { value: 'name_asc', label: 'Name (A-Z)' },
    { value: 'name_desc', label: 'Name (Z-A)' },
    { value: 'createdate_desc', label: 'Date Added (Newest)' },
    { value: 'createdate_asc', label: 'Date Added (Oldest)' },
    { value: 'numberofemployees_desc', label: 'Employees (Most)' },
    { value: 'numberofemployees_asc', label: 'Employees (Least)' },
  ];

  // Deal stages for filter
  const dealStages = [
    { value: '', label: 'All Stages' },
    { value: 'appointmentscheduled', label: 'Appointment Scheduled' },
    { value: 'qualifiedtobuy', label: 'Qualified to Buy' },
    { value: 'presentationscheduled', label: 'Presentation Scheduled' },
    { value: 'decisionmakerboughtin', label: 'Decision Maker Bought-In' },
    { value: 'contractsent', label: 'Contract Sent' },
    { value: 'closedwon', label: 'Closed Won' },
    { value: 'closedlost', label: 'Closed Lost' },
  ];

  // Sort options for Deals
  const dealSortOptions = [
    { value: 'createdate_desc', label: 'Date Added (Newest)' },
    { value: 'createdate_asc', label: 'Date Added (Oldest)' },
    { value: 'amount_desc', label: 'Amount (Highest)' },
    { value: 'amount_asc', label: 'Amount (Lowest)' },
    { value: 'closedate_desc', label: 'Close Date (Latest)' },
    { value: 'closedate_asc', label: 'Close Date (Earliest)' },
  ];

  // Check connection status
  const checkConnection = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/hubspot/status`);
      const data = await res.json();
      setIsConnected(data.connected);
      return data.connected;
    } catch (err) {
      setIsConnected(false);
      return false;
    }
  }, []);

  // Fetch all data
  const fetchData = useCallback(async (filters = {}) => {
    setLoading(true);
    try {
      // Build contacts URL with filters
      const contactParams = new URLSearchParams({ limit: '50' });
      if (filters.search) contactParams.set('search', filters.search);
      if (filters.lifecycleStage) contactParams.set('lifecycle_stage', filters.lifecycleStage);
      if (filters.sortBy) contactParams.set('sort_by', filters.sortBy);
      if (filters.sortOrder) contactParams.set('sort_order', filters.sortOrder);
      
      const [contactsRes, companiesRes, dealsRes, pipelinesRes, ownersRes, statsRes] = await Promise.all([
        fetch(`${API_BASE}/hubspot/contacts?${contactParams.toString()}`),
        fetch(`${API_BASE}/hubspot/companies?limit=50`),
        fetch(`${API_BASE}/hubspot/deals?limit=100`),
        fetch(`${API_BASE}/hubspot/pipelines`),
        fetch(`${API_BASE}/hubspot/owners`),
        fetch(`${API_BASE}/hubspot/stats`),
      ]);

      const [contactsData, companiesData, dealsData, pipelinesData, ownersData, statsData] = await Promise.all([
        contactsRes.json(),
        companiesRes.json(),
        dealsRes.json(),
        pipelinesRes.json(),
        ownersRes.json(),
        statsRes.json(),
      ]);

      setContacts(contactsData.contacts || []);
      setContactsPaging(contactsData.paging || null);
      setCompanies(companiesData.companies || []);
      setCompaniesPaging(companiesData.paging || null);
      setDeals(dealsData.deals || []);
      setPipelines(pipelinesData.pipelines || []);
      setOwners(ownersData.owners || []);

      // Use stats from dedicated endpoint for accurate totals
      setStats({
        totalContacts: statsData.totalContacts || 0,
        totalCompanies: statsData.totalCompanies || 0,
        totalDeals: statsData.totalDeals || 0,
        totalPipeline: statsData.totalPipeline || 0,
      });
    } catch (err) {
      console.error('Failed to fetch HubSpot data:', err);
      toast.error('Failed to load HubSpot data');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch contacts with current filters
  const fetchContacts = useCallback(async (reset = true) => {
    if (reset) setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '50' });
      if (searchQuery) params.set('search', searchQuery);
      if (lifecycleFilter) params.set('lifecycle_stage', lifecycleFilter);
      params.set('sort_by', sortBy);
      params.set('sort_order', sortOrder);
      
      const res = await fetch(`${API_BASE}/hubspot/contacts?${params.toString()}`);
      const data = await res.json();
      setContacts(data.contacts || []);
      setContactsPaging(data.paging || null);
      if (reset) setSelectedContacts(new Set());
    } catch (err) {
      toast.error('Failed to load contacts');
    } finally {
      setLoading(false);
    }
  }, [searchQuery, lifecycleFilter, sortBy, sortOrder]);

  // Load more contacts
  const loadMoreContacts = useCallback(async () => {
    if (!contactsPaging?.next?.after) return;
    setLoadingMore(true);
    try {
      const params = new URLSearchParams({ limit: '50' });
      params.set('after', contactsPaging.next.after);
      if (searchQuery) params.set('search', searchQuery);
      if (lifecycleFilter) params.set('lifecycle_stage', lifecycleFilter);
      params.set('sort_by', sortBy);
      params.set('sort_order', sortOrder);
      
      const res = await fetch(`${API_BASE}/hubspot/contacts?${params.toString()}`);
      const data = await res.json();
      setContacts(prev => [...prev, ...(data.contacts || [])]);
      setContactsPaging(data.paging || null);
    } catch (err) {
      toast.error('Failed to load more contacts');
    } finally {
      setLoadingMore(false);
    }
  }, [contactsPaging, searchQuery, lifecycleFilter, sortBy, sortOrder]);

  // Load more companies
  const loadMoreCompanies = useCallback(async () => {
    if (!companiesPaging?.next?.after) return;
    setLoadingMore(true);
    try {
      const params = new URLSearchParams({ limit: '50' });
      params.set('after', companiesPaging.next.after);
      
      const res = await fetch(`${API_BASE}/hubspot/companies?${params.toString()}`);
      const data = await res.json();
      setCompanies(prev => [...prev, ...(data.companies || [])]);
      setCompaniesPaging(data.paging || null);
    } catch (err) {
      toast.error('Failed to load more companies');
    } finally {
      setLoadingMore(false);
    }
  }, [companiesPaging]);

  // Toggle contact selection
  const toggleContactSelection = (contactId) => {
    setSelectedContacts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(contactId)) {
        newSet.delete(contactId);
      } else {
        newSet.add(contactId);
      }
      return newSet;
    });
  };

  // Toggle company selection
  const toggleCompanySelection = (companyId) => {
    setSelectedCompanies(prev => {
      const newSet = new Set(prev);
      if (newSet.has(companyId)) {
        newSet.delete(companyId);
      } else {
        newSet.add(companyId);
      }
      return newSet;
    });
  };

  // Select all contacts
  const selectAllContacts = () => {
    if (selectedContacts.size === contacts.length) {
      setSelectedContacts(new Set());
    } else {
      setSelectedContacts(new Set(contacts.map(c => c.id)));
    }
  };

  // Select all companies
  const selectAllCompanies = () => {
    if (selectedCompanies.size === companies.length) {
      setSelectedCompanies(new Set());
    } else {
      setSelectedCompanies(new Set(companies.map(c => c.id)));
    }
  };

  // Toggle deal selection
  const toggleDealSelection = (dealId) => {
    setSelectedDeals(prev => {
      const newSet = new Set(prev);
      if (newSet.has(dealId)) {
        newSet.delete(dealId);
      } else {
        newSet.add(dealId);
      }
      return newSet;
    });
  };

  // Select all deals
  const selectAllDeals = () => {
    if (selectedDeals.size === filteredDeals.length) {
      setSelectedDeals(new Set());
    } else {
      setSelectedDeals(new Set(filteredDeals.map(d => d.id)));
    }
  };

  // Filter and sort deals locally
  const filteredDeals = React.useMemo(() => {
    let result = [...deals];
    
    // Filter by search query
    if (dealSearchQuery) {
      const query = dealSearchQuery.toLowerCase();
      result = result.filter(d => 
        d.dealname?.toLowerCase().includes(query) ||
        d.dealstage?.toLowerCase().includes(query)
      );
    }
    
    // Filter by stage
    if (dealStageFilter) {
      result = result.filter(d => d.dealstage?.toLowerCase() === dealStageFilter);
    }
    
    // Sort
    result.sort((a, b) => {
      let aVal, bVal;
      switch (dealSortBy) {
        case 'amount':
          aVal = parseFloat(a.amount) || 0;
          bVal = parseFloat(b.amount) || 0;
          break;
        case 'closedate':
          aVal = new Date(a.closedate || 0).getTime();
          bVal = new Date(b.closedate || 0).getTime();
          break;
        default: // createdate
          aVal = new Date(a.createdate || 0).getTime();
          bVal = new Date(b.createdate || 0).getTime();
      }
      return dealSortOrder === 'asc' ? aVal - bVal : bVal - aVal;
    });
    
    return result;
  }, [deals, dealSearchQuery, dealStageFilter, dealSortBy, dealSortOrder]);

  // Filter and sort companies locally
  const filteredCompanies = React.useMemo(() => {
    let result = [...companies];
    
    // Filter by search query
    if (companySearchQuery) {
      const query = companySearchQuery.toLowerCase();
      result = result.filter(c => 
        c.name?.toLowerCase().includes(query) ||
        c.domain?.toLowerCase().includes(query) ||
        c.industry?.toLowerCase().includes(query)
      );
    }
    
    // Filter by industry
    if (industryFilter) {
      result = result.filter(c => c.industry?.toLowerCase().includes(industryFilter.toLowerCase()));
    }
    
    // Sort
    result.sort((a, b) => {
      let aVal, bVal;
      switch (companySortBy) {
        case 'numberofemployees':
          aVal = parseInt(a.numberofemployees) || 0;
          bVal = parseInt(b.numberofemployees) || 0;
          break;
        case 'createdate':
          aVal = new Date(a.createdate || 0).getTime();
          bVal = new Date(b.createdate || 0).getTime();
          break;
        default: // name
          aVal = (a.name || '').toLowerCase();
          bVal = (b.name || '').toLowerCase();
          return companySortOrder === 'asc' 
            ? aVal.localeCompare(bVal) 
            : bVal.localeCompare(aVal);
      }
      return companySortOrder === 'asc' ? aVal - bVal : bVal - aVal;
    });
    
    return result;
  }, [companies, companySearchQuery, industryFilter, companySortBy, companySortOrder]);

  // Handle company sort change
  const handleCompanySortChange = (value) => {
    const [field, order] = value.split('_');
    setCompanySortBy(field);
    setCompanySortOrder(order);
  };

  // Handle deal sort change
  const handleDealSortChange = (value) => {
    const [field, order] = value.split('_');
    setDealSortBy(field);
    setDealSortOrder(order);
  };

  // Load folders from localStorage
  useEffect(() => {
    const savedFolders = localStorage.getItem('hubspot_folders');
    if (savedFolders) {
      setFolders(JSON.parse(savedFolders));
    }
  }, []);

  // Save folders to localStorage
  const saveFolders = (newFolders) => {
    setFolders(newFolders);
    localStorage.setItem('hubspot_folders', JSON.stringify(newFolders));
  };

  // Create new folder
  const createFolder = () => {
    if (!newFolderName.trim()) {
      toast.error('Please enter a folder name');
      return;
    }
    const newFolder = {
      id: Date.now().toString(),
      name: newFolderName.trim(),
      contacts: [],
      companies: [],
      createdAt: new Date().toISOString(),
    };
    saveFolders([...folders, newFolder]);
    setNewFolderName('');
    setShowCreateFolder(false);
    toast.success(`Folder "${newFolder.name}" created`);
  };

  // Add selected items to folder
  const addToFolder = (folderId) => {
    const folder = folders.find(f => f.id === folderId);
    if (!folder) return;

    const updatedFolders = folders.map(f => {
      if (f.id === folderId) {
        const newContacts = [...new Set([...f.contacts, ...Array.from(selectedContacts)])];
        const newCompanies = [...new Set([...f.companies, ...Array.from(selectedCompanies)])];
        return { ...f, contacts: newContacts, companies: newCompanies };
      }
      return f;
    });

    saveFolders(updatedFolders);
    const addedCount = selectedContacts.size + selectedCompanies.size;
    toast.success(`Added ${addedCount} item${addedCount !== 1 ? 's' : ''} to "${folder.name}"`);
    setSelectedContacts(new Set());
    setSelectedCompanies(new Set());
    setShowFolderModal(false);
  };

  // Delete folder
  const deleteFolder = (folderId) => {
    const folder = folders.find(f => f.id === folderId);
    if (!folder) return;
    saveFolders(folders.filter(f => f.id !== folderId));
    toast.success(`Folder "${folder.name}" deleted`);
  };

  // Initial load
  useEffect(() => {
    const init = async () => {
      const connected = await checkConnection();
      if (connected) {
        await fetchData();
      } else {
        setLoading(false);
      }
    };
    init();
  }, [checkConnection, fetchData]);

  // Refetch contacts when filters change
  useEffect(() => {
    if (isConnected && activeTab === 'contacts') {
      const timer = setTimeout(() => {
        fetchContacts();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [searchQuery, lifecycleFilter, sortBy, sortOrder, isConnected, activeTab, fetchContacts]);

  // Handle sort change
  const handleSortChange = (value) => {
    const [field, order] = value.split('_');
    setSortBy(field);
    setSortOrder(order);
  };

  // Folder Modal Component
  const FolderModal = () => (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-[#111] border border-white/10 rounded-2xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-white flex items-center gap-2">
            <FolderPlus className="w-5 h-5 text-orange-400" />
            Add to Folder
          </h2>
          <button onClick={() => setShowFolderModal(false)} className="text-gray-400 hover:text-white">
            <X size={20} />
          </button>
        </div>
        
        <p className="text-sm text-gray-400 mb-4">
          {selectedContacts.size + selectedCompanies.size} item{(selectedContacts.size + selectedCompanies.size) !== 1 ? 's' : ''} selected
        </p>

        {/* Create New Folder */}
        {showCreateFolder ? (
          <div className="mb-4 p-3 bg-black/50 rounded-xl">
            <Input
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              placeholder="Folder name..."
              className="bg-black border-white/20 text-white mb-2"
              autoFocus
              onKeyDown={(e) => e.key === 'Enter' && createFolder()}
            />
            <div className="flex gap-2">
              <Button onClick={createFolder} size="sm" className="bg-orange-500 hover:bg-orange-600 text-white">
                Create
              </Button>
              <Button onClick={() => { setShowCreateFolder(false); setNewFolderName(''); }} size="sm" variant="outline" className="border-white/20 text-white">
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setShowCreateFolder(true)}
            className="w-full mb-4 p-3 border border-dashed border-white/20 rounded-xl text-gray-400 hover:text-white hover:border-orange-500/50 transition-colors flex items-center justify-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Create New Folder
          </button>
        )}

        {/* Existing Folders */}
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {folders.length === 0 ? (
            <p className="text-center text-gray-500 py-4">No folders yet. Create one above.</p>
          ) : (
            folders.map((folder) => (
              <div
                key={folder.id}
                className="flex items-center justify-between p-3 bg-black/50 rounded-xl hover:bg-white/5 transition-colors group"
              >
                <button
                  onClick={() => addToFolder(folder.id)}
                  className="flex items-center gap-3 flex-1 text-left"
                >
                  <Folder className="w-5 h-5 text-orange-400" />
                  <div>
                    <p className="text-white font-medium">{folder.name}</p>
                    <p className="text-xs text-gray-500">
                      {folder.contacts.length} contacts, {folder.companies.length} companies
                    </p>
                  </div>
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); deleteFolder(folder.id); }}
                  className="text-gray-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity p-1"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );

  // Create contact form
  const CreateContactModal = () => {
    const [form, setForm] = useState({
      email: '',
      firstname: '',
      lastname: '',
      phone: '',
      company: '',
      jobtitle: '',
    });
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async (e) => {
      e.preventDefault();
      if (!form.email) {
        toast.error('Email is required');
        return;
      }
      setSubmitting(true);
      try {
        const res = await fetch(`${API_BASE}/hubspot/contacts`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(form),
        });
        const data = await res.json();
        if (data.ok) {
          toast.success('Contact created successfully');
          setShowCreateContact(false);
          fetchData();
        } else {
          throw new Error(data.detail || 'Failed to create contact');
        }
      } catch (err) {
        toast.error(err.message);
      } finally {
        setSubmitting(false);
      }
    };

    return (
      <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
        <div className="bg-[#111] border border-white/10 rounded-2xl w-full max-w-md p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-white">Create Contact</h2>
            <button onClick={() => setShowCreateContact(false)} className="text-gray-400 hover:text-white">
              <X size={20} />
            </button>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Email *</label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="email@example.com"
                className="bg-black border-white/20 text-white"
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-400 mb-1 block">First Name</label>
                <Input
                  value={form.firstname}
                  onChange={(e) => setForm({ ...form, firstname: e.target.value })}
                  placeholder="John"
                  className="bg-black border-white/20 text-white"
                />
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Last Name</label>
                <Input
                  value={form.lastname}
                  onChange={(e) => setForm({ ...form, lastname: e.target.value })}
                  placeholder="Doe"
                  className="bg-black border-white/20 text-white"
                />
              </div>
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Phone</label>
              <Input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="+1 (555) 123-4567"
                className="bg-black border-white/20 text-white"
              />
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Company</label>
              <Input
                value={form.company}
                onChange={(e) => setForm({ ...form, company: e.target.value })}
                placeholder="Acme Inc."
                className="bg-black border-white/20 text-white"
              />
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Job Title</label>
              <Input
                value={form.jobtitle}
                onChange={(e) => setForm({ ...form, jobtitle: e.target.value })}
                placeholder="CEO"
                className="bg-black border-white/20 text-white"
              />
            </div>
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowCreateContact(false)}
                className="flex-1 border-white/20 text-white hover:bg-white/10"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="flex-1 bg-orange-500 hover:bg-orange-600 text-white"
              >
                {submitting ? 'Creating...' : 'Create Contact'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  // Contact detail modal
  const ContactDetailModal = ({ contact, onClose }) => {
    if (!contact) return null;
    
    const fullName = [contact.firstname, contact.lastname].filter(Boolean).join(' ') || 'Unnamed Contact';
    
    const initials = (contact.firstname?.[0] || contact.email?.[0] || '?').toUpperCase();
    
    return (
      <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
        <div className="bg-[#111] border border-white/10 rounded-2xl w-full max-w-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-14 h-14 bg-orange-500/20 rounded-full flex items-center justify-center overflow-hidden">
                {contact.avatar_url ? (
                  <img 
                    src={contact.avatar_url} 
                    alt={fullName}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentNode.querySelector('.fallback-icon').style.display = 'flex';
                    }}
                  />
                ) : null}
                <div className={`fallback-icon w-full h-full items-center justify-center ${contact.avatar_url ? 'hidden' : 'flex'}`}>
                  <User className="w-7 h-7 text-orange-400" />
                </div>
              </div>
              <div>
                <h2 className="text-xl font-semibold text-white">{fullName}</h2>
                {contact.jobtitle && <p className="text-sm text-gray-400">{contact.jobtitle}</p>}
              </div>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-white">
              <X size={20} />
            </button>
          </div>
          
          <div className="space-y-4">
            {contact.email && (
              <div className="flex items-center gap-3 p-3 bg-black/50 rounded-xl">
                <Mail className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-xs text-gray-500">Email</p>
                  <a href={`mailto:${contact.email}`} className="text-white hover:text-orange-400">
                    {contact.email}
                  </a>
                </div>
              </div>
            )}
            {contact.phone && (
              <div className="flex items-center gap-3 p-3 bg-black/50 rounded-xl">
                <Phone className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-xs text-gray-500">Phone</p>
                  <a href={`tel:${contact.phone}`} className="text-white hover:text-orange-400">
                    {contact.phone}
                  </a>
                </div>
              </div>
            )}
            {contact.company && (
              <div className="flex items-center gap-3 p-3 bg-black/50 rounded-xl">
                <Building className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-xs text-gray-500">Company</p>
                  <p className="text-white">{contact.company}</p>
                </div>
              </div>
            )}
            {contact.lifecyclestage && (
              <div className="flex items-center gap-3 p-3 bg-black/50 rounded-xl">
                <TrendingUp className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-xs text-gray-500">Lifecycle Stage</p>
                  <LifecycleBadge stage={contact.lifecyclestage} />
                </div>
              </div>
            )}
            {contact.createdate && (
              <div className="flex items-center gap-3 p-3 bg-black/50 rounded-xl">
                <Calendar className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-xs text-gray-500">Created</p>
                  <p className="text-white">{formatDate(contact.createdate)}</p>
                </div>
              </div>
            )}
          </div>
          
          <div className="flex gap-3 mt-6">
            <Button
              variant="outline"
              className="flex-1 border-white/20 text-white hover:bg-white/10"
              onClick={() => window.open(`https://app.hubspot.com/contacts/${contact.id}`, '_blank')}
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              Open in HubSpot
            </Button>
          </div>
        </div>
      </div>
    );
  };

  // Not connected state
  if (isConnected === false) {
    return (
      <div className="min-h-screen bg-black p-6">
        <div className="max-w-2xl mx-auto mt-20">
          <div className="bg-[#111] border border-white/10 rounded-2xl p-8 text-center">
            <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-400" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">HubSpot Not Connected</h2>
            <p className="text-gray-400 mb-6">
              Your HubSpot integration is not configured. Please contact your administrator to set up the connection.
            </p>
            <Button onClick={checkConnection} variant="outline" className="border-white/20 text-white hover:bg-white/10">
              <RefreshCw className="w-4 h-4 mr-2" />
              Retry Connection
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black p-4 md:p-6" data-testid="hubspot-page">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-500/20 rounded-xl flex items-center justify-center">
                <img 
                  src="https://www.hubspot.com/hubfs/HubSpot_Logos/HubSpot-Inversed-Favicon.png" 
                  alt="HubSpot" 
                  className="w-6 h-6"
                />
              </div>
              HubSpot CRM
            </h1>
            <p className="text-gray-400 mt-1">Manage your contacts, companies, and deals</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 px-3 py-1.5 bg-green-500/20 border border-green-500/30 rounded-full">
              <CheckCircle2 className="w-4 h-4 text-green-400" />
              <span className="text-sm text-green-400">Connected</span>
            </div>
            <Button 
              onClick={fetchData} 
              variant="outline" 
              className="border-white/20 text-white hover:bg-white/10"
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-[#111] border border-white/10 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{stats.totalContacts}</p>
              <p className="text-sm text-gray-400">Contacts</p>
            </div>
          </div>
        </div>
        <div className="bg-[#111] border border-white/10 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
              <Building2 className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{stats.totalCompanies}</p>
              <p className="text-sm text-gray-400">Companies</p>
            </div>
          </div>
        </div>
        <div className="bg-[#111] border border-white/10 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
              <Briefcase className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{stats.totalDeals}</p>
              <p className="text-sm text-gray-400">Deals</p>
            </div>
          </div>
        </div>
        <div className="bg-[#111] border border-white/10 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-500/20 rounded-lg flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-orange-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{formatCurrency(stats.totalPipeline)}</p>
              <p className="text-sm text-gray-400">Pipeline Value</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-white/10 pb-4">
        {[
          { id: 'contacts', label: 'Contacts', icon: Users },
          { id: 'companies', label: 'Companies', icon: Building2 },
          { id: 'deals', label: 'Deals', icon: Briefcase },
          { id: 'folders', label: 'Folders', icon: Folder },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
              activeTab === tab.id
                ? 'bg-orange-500 text-white'
                : 'text-gray-400 hover:text-white hover:bg-white/5'
            }`}
            data-testid={`tab-${tab.id}`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
            {tab.id === 'folders' && folders.length > 0 && (
              <span className="ml-1 px-1.5 py-0.5 text-xs bg-white/20 rounded-full">{folders.length}</span>
            )}
          </button>
        ))}
      </div>

      {/* Search & Actions */}
      {activeTab === 'contacts' && (
        <div className="space-y-4 mb-6">
          {/* Selection Bar - shows when items are selected */}
          {selectedContacts.size > 0 && (
            <div className="flex items-center gap-4 p-3 bg-orange-500/10 border border-orange-500/30 rounded-xl">
              <span className="text-orange-400 font-medium">
                {selectedContacts.size} selected
              </span>
              <Button 
                onClick={() => setShowFolderModal(true)}
                size="sm"
                className="bg-orange-500 hover:bg-orange-600 text-white"
              >
                <FolderPlus className="w-4 h-4 mr-2" />
                Add to Folder
              </Button>
              <button 
                onClick={() => setSelectedContacts(new Set())}
                className="text-sm text-gray-400 hover:text-white ml-auto"
              >
                Clear selection
              </button>
            </div>
          )}

          {/* Search Row */}
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search contacts by email..."
                className="pl-10 bg-[#111] border-white/10 text-white placeholder:text-gray-500"
                data-testid="search-contacts"
              />
            </div>
            <Button 
              onClick={() => setShowCreateContact(true)}
              className="bg-orange-500 hover:bg-orange-600 text-white"
              data-testid="create-contact-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Contact
            </Button>
          </div>
          
          {/* Filters Row */}
          <div className="flex flex-wrap gap-3 items-center">
            {/* Select All Checkbox */}
            <button
              onClick={selectAllContacts}
              className="flex items-center gap-2 text-sm text-gray-400 hover:text-white"
            >
              <div className={`w-5 h-5 rounded border ${selectedContacts.size === contacts.length && contacts.length > 0 ? 'bg-orange-500 border-orange-500' : 'border-white/30'} flex items-center justify-center`}>
                {selectedContacts.size === contacts.length && contacts.length > 0 && <Check className="w-3 h-3 text-white" />}
              </div>
              Select All
            </button>

            <div className="w-px h-6 bg-white/10" />

            {/* Lifecycle Stage Filter */}
            <select
              value={lifecycleFilter}
              onChange={(e) => setLifecycleFilter(e.target.value)}
              className="bg-[#111] border border-white/10 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-orange-500/50"
              data-testid="filter-lifecycle"
            >
              {lifecycleStages.map((stage) => (
                <option key={stage.value} value={stage.value} className="bg-[#111]">
                  {stage.label}
                </option>
              ))}
            </select>
            
            {/* Sort By */}
            <select
              value={`${sortBy}_${sortOrder}`}
              onChange={(e) => handleSortChange(e.target.value)}
              className="bg-[#111] border border-white/10 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-orange-500/50"
              data-testid="filter-sort"
            >
              {sortOptions.map((opt) => (
                <option key={opt.value} value={opt.value} className="bg-[#111]">
                  {opt.label}
                </option>
              ))}
            </select>
            
            {/* Clear Filters */}
            {(searchQuery || lifecycleFilter) && (
              <button
                onClick={() => {
                  setSearchQuery('');
                  setLifecycleFilter('');
                  setSortBy('lastmodifieddate');
                  setSortOrder('desc');
                }}
                className="text-sm text-gray-400 hover:text-white flex items-center gap-1"
              >
                <X className="w-4 h-4" />
                Clear filters
              </button>
            )}
            
            {/* View Toggle & Results count */}
            <div className="flex items-center gap-2 ml-auto">
              <div className="flex items-center bg-[#111] border border-white/10 rounded-lg p-1">
                <button
                  onClick={() => setContactsViewMode('list')}
                  className={`p-1.5 rounded ${contactsViewMode === 'list' ? 'bg-orange-500 text-white' : 'text-gray-400 hover:text-white'}`}
                  title="List View"
                >
                  <LayoutList className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setContactsViewMode('grid')}
                  className={`p-1.5 rounded ${contactsViewMode === 'grid' ? 'bg-orange-500 text-white' : 'text-gray-400 hover:text-white'}`}
                  title="Grid View"
                >
                  <LayoutGrid className="w-4 h-4" />
                </button>
              </div>
              <span className="text-sm text-gray-500">
                {contacts.length} of {stats.totalContacts}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <RefreshCw className="w-8 h-8 text-orange-400 animate-spin" />
        </div>
      ) : (
        <>
          {/* Contacts Tab */}
          {activeTab === 'contacts' && (
            <div className="space-y-4">
              {/* Grid View */}
              {contactsViewMode === 'grid' ? (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                  {contacts.length === 0 ? (
                    <div className="col-span-full text-center py-12 text-gray-400">
                      No contacts found. Create your first contact to get started.
                    </div>
                  ) : (
                    contacts.map((contact) => {
                      const fullName = [contact.firstname, contact.lastname].filter(Boolean).join(' ') || 'Unnamed';
                      const initials = (contact.firstname?.[0] || contact.email?.[0] || '?').toUpperCase();
                      const isSelected = selectedContacts.has(contact.id);
                      return (
                        <div
                          key={contact.id}
                          className={`bg-[#111] border rounded-xl p-4 transition-all group relative ${isSelected ? 'border-orange-500 bg-orange-500/5' : 'border-white/10 hover:border-orange-500/50'}`}
                          data-testid={`contact-grid-${contact.id}`}
                        >
                          {/* Checkbox */}
                          <button
                            onClick={(e) => { e.stopPropagation(); toggleContactSelection(contact.id); }}
                            className={`absolute top-3 left-3 w-5 h-5 rounded border ${isSelected ? 'bg-orange-500 border-orange-500' : 'border-white/30 hover:border-orange-500 bg-black/50'} flex items-center justify-center transition-colors z-10`}
                          >
                            {isSelected && <Check className="w-3 h-3 text-white" />}
                          </button>
                          
                          <div onClick={() => setSelectedContact(contact)} className="cursor-pointer">
                            {/* Company Logo */}
                            <CompanyLogo
                              logoUrl={contact.company_logo_url}
                              domain={contact.company_domain}
                              name={contact.company}
                              initials={initials}
                              size="lg"
                              className="mx-auto mb-3"
                            />
                            
                            {/* Name */}
                            <h3 className="text-white font-medium text-center truncate group-hover:text-orange-400 transition-colors">
                              {fullName}
                            </h3>
                            
                            {/* Company */}
                            {contact.company && (
                              <p className="text-xs text-gray-500 text-center truncate mt-1">
                                {contact.company}
                              </p>
                            )}
                            
                            {/* Email */}
                            <p className="text-xs text-gray-400 text-center truncate mt-1">
                              {contact.email || 'No email'}
                            </p>
                            
                            {/* Stage Badge */}
                            {contact.lifecyclestage && (
                              <div className="mt-2 flex justify-center">
                                <LifecycleBadge stage={contact.lifecyclestage} />
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              ) : (
                /* List View */
                <div className="grid gap-3">
                  {contacts.length === 0 ? (
                    <div className="text-center py-12 text-gray-400">
                      No contacts found. Create your first contact to get started.
                    </div>
                  ) : (
                    contacts.map((contact) => {
                      const fullName = [contact.firstname, contact.lastname].filter(Boolean).join(' ') || 'Unnamed';
                      const initials = (contact.firstname?.[0] || contact.email?.[0] || '?').toUpperCase();
                      const isSelected = selectedContacts.has(contact.id);
                      return (
                        <div
                          key={contact.id}
                          className={`bg-[#111] border rounded-xl p-4 transition-all group ${isSelected ? 'border-orange-500 bg-orange-500/5' : 'border-white/10 hover:border-orange-500/50'}`}
                          data-testid={`contact-${contact.id}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              {/* Checkbox */}
                              <button
                                onClick={(e) => { e.stopPropagation(); toggleContactSelection(contact.id); }}
                                className={`w-6 h-6 rounded border ${isSelected ? 'bg-orange-500 border-orange-500' : 'border-white/30 hover:border-orange-500'} flex items-center justify-center transition-colors`}
                              >
                                {isSelected && <Check className="w-4 h-4 text-white" />}
                              </button>
                              
                              {/* Company Logo in Main Circle */}
                              <div onClick={() => setSelectedContact(contact)} className="cursor-pointer">
                                <CompanyLogo
                                  logoUrl={contact.company_logo_url}
                                  domain={contact.company_domain}
                                  name={contact.company}
                                  initials={initials}
                                  size="md"
                                />
                              </div>
                              <div onClick={() => setSelectedContact(contact)} className="cursor-pointer">
                                <h3 className="text-white font-medium group-hover:text-orange-400 transition-colors">
                                  {fullName}
                                </h3>
                                <p className="text-sm text-gray-400">{contact.email || 'No email'}</p>
                                <div className="flex items-center gap-3 mt-1 flex-wrap">
                                  {contact.company && (
                                    <span className="text-xs text-gray-500 flex items-center gap-1">
                                      <Building2 className="w-3 h-3" />
                                      {contact.company}
                                    </span>
                                  )}
                                  {contact.createdate && (
                                    <span className="text-xs text-gray-500 flex items-center gap-1">
                                      <Calendar className="w-3 h-3" />
                                      Added {formatDateTime(contact.createdate)}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-4">
                              {contact.lifecyclestage && (
                                <LifecycleBadge stage={contact.lifecyclestage} />
                              )}
                              <ChevronRight 
                                onClick={() => setSelectedContact(contact)}
                                className="w-5 h-5 text-gray-500 group-hover:text-orange-400 transition-colors cursor-pointer" 
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              )}
              
              {/* Load More Button */}
              {contactsPaging?.next?.after && (
                <div className="flex justify-center pt-4">
                  <Button
                    onClick={loadMoreContacts}
                    disabled={loadingMore}
                    variant="outline"
                    className="border-white/20 text-white hover:bg-white/10"
                  >
                    {loadingMore ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Loading...
                      </>
                    ) : (
                      <>
                        <ChevronDown className="w-4 h-4 mr-2" />
                        Load More Contacts ({contacts.length} of {stats.totalContacts})
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Companies Tab */}
          {activeTab === 'companies' && (
            <div className="space-y-4">
              {/* Selection Bar for Companies */}
              {selectedCompanies.size > 0 && (
                <div className="flex items-center gap-4 p-3 bg-purple-500/10 border border-purple-500/30 rounded-xl">
                  <span className="text-purple-400 font-medium">
                    {selectedCompanies.size} selected
                  </span>
                  <Button 
                    onClick={() => setShowFolderModal(true)}
                    size="sm"
                    className="bg-purple-500 hover:bg-purple-600 text-white"
                  >
                    <FolderPlus className="w-4 h-4 mr-2" />
                    Add to Folder
                  </Button>
                  <button 
                    onClick={() => setSelectedCompanies(new Set())}
                    className="text-sm text-gray-400 hover:text-white ml-auto"
                  >
                    Clear selection
                  </button>
                </div>
              )}

              {/* Search Bar for Companies */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  value={companySearchQuery}
                  onChange={(e) => setCompanySearchQuery(e.target.value)}
                  placeholder="Search companies by name, domain, or industry..."
                  className="pl-10 bg-[#111] border-white/10 text-white placeholder:text-gray-500"
                  data-testid="search-companies"
                />
              </div>

              {/* Filter Bar for Companies */}
              <div className="flex flex-wrap items-center gap-3 mb-2">
                {/* Select All Checkbox */}
                <button
                  onClick={selectAllCompanies}
                  className="flex items-center gap-2 text-sm text-gray-400 hover:text-white"
                  data-testid="companies-select-all"
                >
                  <div className={`w-5 h-5 rounded border ${selectedCompanies.size === filteredCompanies.length && filteredCompanies.length > 0 ? 'bg-purple-500 border-purple-500' : 'border-white/30'} flex items-center justify-center`}>
                    {selectedCompanies.size === filteredCompanies.length && filteredCompanies.length > 0 && <Check className="w-3 h-3 text-white" />}
                  </div>
                  Select All
                </button>

                <div className="w-px h-6 bg-white/10" />

                {/* Industry Filter */}
                <Input
                  value={industryFilter}
                  onChange={(e) => setIndustryFilter(e.target.value)}
                  placeholder="Filter by industry..."
                  className="w-40 bg-[#111] border-white/10 text-white text-sm h-9 placeholder:text-gray-500"
                  data-testid="companies-industry-filter"
                />

                {/* Sort By */}
                <select
                  value={`${companySortBy}_${companySortOrder}`}
                  onChange={(e) => handleCompanySortChange(e.target.value)}
                  className="bg-[#111] border border-white/10 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-purple-500/50"
                  data-testid="companies-sort"
                >
                  {companySortOptions.map((opt) => (
                    <option key={opt.value} value={opt.value} className="bg-[#111]">
                      {opt.label}
                    </option>
                  ))}
                </select>

                {/* Clear Filters */}
                {(industryFilter || companySearchQuery) && (
                  <button
                    onClick={() => {
                      setIndustryFilter('');
                      setCompanySearchQuery('');
                      setCompanySortBy('name');
                      setCompanySortOrder('asc');
                    }}
                    className="text-sm text-gray-400 hover:text-white flex items-center gap-1"
                  >
                    <X className="w-4 h-4" />
                    Clear filters
                  </button>
                )}
                
                {/* View Toggle & Count */}
                <div className="flex items-center gap-2 ml-auto">
                  <div className="flex items-center bg-[#111] border border-white/10 rounded-lg p-1">
                    <button
                      onClick={() => setCompaniesViewMode('list')}
                      className={`p-1.5 rounded ${companiesViewMode === 'list' ? 'bg-purple-500 text-white' : 'text-gray-400 hover:text-white'}`}
                      title="List View"
                      data-testid="companies-view-list"
                    >
                      <LayoutList className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setCompaniesViewMode('grid')}
                      className={`p-1.5 rounded ${companiesViewMode === 'grid' ? 'bg-purple-500 text-white' : 'text-gray-400 hover:text-white'}`}
                      title="Grid View"
                      data-testid="companies-view-grid"
                    >
                      <LayoutGrid className="w-4 h-4" />
                    </button>
                  </div>
                  <span className="text-sm text-gray-500">
                    {filteredCompanies.length} of {stats.totalCompanies}
                  </span>
                </div>
              </div>

              {/* Grid View for Companies */}
              {companiesViewMode === 'grid' ? (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                  {filteredCompanies.length === 0 ? (
                    <div className="col-span-full text-center py-12 text-gray-400">
                      {industryFilter ? 'No companies match your filter.' : 'No companies found in your HubSpot account.'}
                    </div>
                  ) : (
                    filteredCompanies.map((company) => {
                      const isSelected = selectedCompanies.has(company.id);
                      const initials = (company.name?.[0] || '?').toUpperCase();
                      return (
                        <div
                          key={company.id}
                          className={`bg-[#111] border rounded-xl p-4 transition-all group relative ${isSelected ? 'border-purple-500 bg-purple-500/5' : 'border-white/10 hover:border-purple-500/50'}`}
                          data-testid={`company-grid-${company.id}`}
                        >
                          {/* Checkbox */}
                          <button
                            onClick={(e) => { e.stopPropagation(); toggleCompanySelection(company.id); }}
                            className={`absolute top-3 left-3 w-5 h-5 rounded border ${isSelected ? 'bg-purple-500 border-purple-500' : 'border-white/30 hover:border-purple-500 bg-black/50'} flex items-center justify-center transition-colors z-10`}
                          >
                            {isSelected && <Check className="w-3 h-3 text-white" />}
                          </button>
                          
                          <div 
                            onClick={() => window.open(`https://app.hubspot.com/contacts/companies/${company.id}`, '_blank')}
                            className="cursor-pointer"
                          >
                            {/* Company Logo */}
                            <CompanyLogo
                              logoUrl={company.logo_url}
                              domain={company.domain}
                              name={company.name}
                              initials={initials}
                              size="lg"
                              className="mx-auto mb-3 bg-purple-500/20"
                            />
                            
                            {/* Company Name */}
                            <h3 className="text-white font-medium text-center truncate group-hover:text-purple-400 transition-colors">
                              {company.name || 'Unnamed Company'}
                            </h3>
                            
                            {/* Domain */}
                            {company.domain && (
                              <p className="text-xs text-gray-400 text-center truncate mt-1 flex items-center justify-center gap-1">
                                <Globe className="w-3 h-3" />
                                {company.domain}
                              </p>
                            )}
                            
                            {/* Industry */}
                            {company.industry && (
                              <p className="text-xs text-gray-500 text-center truncate mt-1">
                                {company.industry}
                              </p>
                            )}
                            
                            {/* Employee Count */}
                            {company.numberofemployees && (
                              <p className="text-xs text-purple-400 text-center mt-2">
                                {company.numberofemployees} employees
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              ) : (
                /* List View for Companies */
                <div className="grid gap-3">
                  {filteredCompanies.length === 0 ? (
                    <div className="text-center py-12 text-gray-400">
                      {industryFilter ? 'No companies match your filter.' : 'No companies found in your HubSpot account.'}
                    </div>
                  ) : (
                    filteredCompanies.map((company) => {
                      const isSelected = selectedCompanies.has(company.id);
                      return (
                        <div
                          key={company.id}
                          className={`bg-[#111] border rounded-xl p-4 transition-all group ${isSelected ? 'border-purple-500 bg-purple-500/5' : 'border-white/10 hover:border-purple-500/50'}`}
                          data-testid={`company-${company.id}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              {/* Checkbox */}
                              <button
                                onClick={(e) => { e.stopPropagation(); toggleCompanySelection(company.id); }}
                                className={`w-6 h-6 rounded border ${isSelected ? 'bg-purple-500 border-purple-500' : 'border-white/30 hover:border-purple-500'} flex items-center justify-center transition-colors`}
                              >
                                {isSelected && <Check className="w-4 h-4 text-white" />}
                              </button>
                            
                            <div onClick={() => window.open(`https://app.hubspot.com/contacts/companies/${company.id}`, '_blank')} className="cursor-pointer">
                              <CompanyLogo
                                logoUrl={company.logo_url}
                                domain={company.domain}
                                name={company.name}
                                initials={(company.name?.[0] || '?').toUpperCase()}
                                size="sm"
                                className="bg-purple-500/20"
                              />
                            </div>
                            <div 
                              onClick={() => window.open(`https://app.hubspot.com/contacts/companies/${company.id}`, '_blank')}
                              className="cursor-pointer"
                            >
                              <h3 className="text-white font-medium group-hover:text-purple-400 transition-colors">
                                {company.name || 'Unnamed Company'}
                              </h3>
                              {company.domain && (
                                <p className="text-sm text-gray-400">{company.domain}</p>
                              )}
                              <div className="flex items-center gap-4 mt-1 text-xs text-gray-500">
                                {company.industry && <span>{company.industry}</span>}
                                {company.city && <span>{company.city}, {company.state}</span>}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            {company.numberofemployees && (
                              <span className="text-sm text-gray-400">{company.numberofemployees} employees</span>
                            )}
                            <ExternalLink 
                              onClick={() => window.open(`https://app.hubspot.com/contacts/companies/${company.id}`, '_blank')}
                              className="w-5 h-5 text-gray-500 group-hover:text-purple-400 transition-colors cursor-pointer" 
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
              )}

              {/* Load More Companies */}
              {companiesPaging?.next?.after && (
                <div className="flex justify-center pt-4">
                  <Button
                    onClick={loadMoreCompanies}
                    disabled={loadingMore}
                    variant="outline"
                    className="border-white/20 text-white hover:bg-white/10"
                  >
                    {loadingMore ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Loading...
                      </>
                    ) : (
                      <>
                        <ChevronDown className="w-4 h-4 mr-2" />
                        Load More Companies ({companies.length} of {stats.totalCompanies})
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Deals Tab */}
          {activeTab === 'deals' && (
            <div className="space-y-4">
              {/* Selection Bar for Deals */}
              {selectedDeals.size > 0 && (
                <div className="flex items-center gap-4 p-3 bg-green-500/10 border border-green-500/30 rounded-xl">
                  <span className="text-green-400 font-medium">
                    {selectedDeals.size} selected
                  </span>
                  <button 
                    onClick={() => setSelectedDeals(new Set())}
                    className="text-sm text-gray-400 hover:text-white ml-auto"
                  >
                    Clear selection
                  </button>
                </div>
              )}

              {/* Search Bar for Deals */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  value={dealSearchQuery}
                  onChange={(e) => setDealSearchQuery(e.target.value)}
                  placeholder="Search deals by name..."
                  className="pl-10 bg-[#111] border-white/10 text-white placeholder:text-gray-500"
                  data-testid="search-deals"
                />
              </div>

              {/* Filter Bar for Deals */}
              <div className="flex flex-wrap items-center gap-3 mb-2">
                {/* Select All Checkbox */}
                <button
                  onClick={selectAllDeals}
                  className="flex items-center gap-2 text-sm text-gray-400 hover:text-white"
                  data-testid="deals-select-all"
                >
                  <div className={`w-5 h-5 rounded border ${selectedDeals.size === filteredDeals.length && filteredDeals.length > 0 ? 'bg-green-500 border-green-500' : 'border-white/30'} flex items-center justify-center`}>
                    {selectedDeals.size === filteredDeals.length && filteredDeals.length > 0 && <Check className="w-3 h-3 text-white" />}
                  </div>
                  Select All
                </button>

                <div className="w-px h-6 bg-white/10" />

                {/* Stage Filter */}
                <select
                  value={dealStageFilter}
                  onChange={(e) => setDealStageFilter(e.target.value)}
                  className="bg-[#111] border border-white/10 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-green-500/50"
                  data-testid="deals-stage-filter"
                >
                  {dealStages.map((stage) => (
                    <option key={stage.value} value={stage.value} className="bg-[#111]">
                      {stage.label}
                    </option>
                  ))}
                </select>

                {/* Sort By */}
                <select
                  value={`${dealSortBy}_${dealSortOrder}`}
                  onChange={(e) => handleDealSortChange(e.target.value)}
                  className="bg-[#111] border border-white/10 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-green-500/50"
                  data-testid="deals-sort"
                >
                  {dealSortOptions.map((opt) => (
                    <option key={opt.value} value={opt.value} className="bg-[#111]">
                      {opt.label}
                    </option>
                  ))}
                </select>

                {/* Clear Filters */}
                {(dealStageFilter || dealSearchQuery) && (
                  <button
                    onClick={() => {
                      setDealStageFilter('');
                      setDealSearchQuery('');
                      setDealSortBy('createdate');
                      setDealSortOrder('desc');
                    }}
                    className="text-sm text-gray-400 hover:text-white flex items-center gap-1"
                  >
                    <X className="w-4 h-4" />
                    Clear filters
                  </button>
                )}
                
                {/* View Toggle & Count */}
                <div className="flex items-center gap-2 ml-auto">
                  <div className="flex items-center bg-[#111] border border-white/10 rounded-lg p-1">
                    <button
                      onClick={() => setDealsViewMode('list')}
                      className={`p-1.5 rounded ${dealsViewMode === 'list' ? 'bg-green-500 text-white' : 'text-gray-400 hover:text-white'}`}
                      title="List View"
                      data-testid="deals-view-list"
                    >
                      <LayoutList className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setDealsViewMode('grid')}
                      className={`p-1.5 rounded ${dealsViewMode === 'grid' ? 'bg-green-500 text-white' : 'text-gray-400 hover:text-white'}`}
                      title="Grid View"
                      data-testid="deals-view-grid"
                    >
                      <LayoutGrid className="w-4 h-4" />
                    </button>
                  </div>
                  <span className="text-sm text-gray-500">
                    {filteredDeals.length} of {stats.totalDeals}
                  </span>
                </div>
              </div>

              {/* Grid View for Deals */}
              {dealsViewMode === 'grid' ? (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {filteredDeals.length === 0 ? (
                    <div className="col-span-full text-center py-12 text-gray-400">
                      {dealStageFilter ? 'No deals match your filter.' : 'No deals found in your HubSpot account.'}
                    </div>
                  ) : (
                    filteredDeals.map((deal) => {
                      const isSelected = selectedDeals.has(deal.id);
                      return (
                        <div
                          key={deal.id}
                          className={`bg-[#111] border rounded-xl p-4 transition-all group relative ${isSelected ? 'border-green-500 bg-green-500/5' : 'border-white/10 hover:border-green-500/50'}`}
                          data-testid={`deal-grid-${deal.id}`}
                        >
                          {/* Checkbox */}
                          <button
                            onClick={(e) => { e.stopPropagation(); toggleDealSelection(deal.id); }}
                            className={`absolute top-3 left-3 w-5 h-5 rounded border ${isSelected ? 'bg-green-500 border-green-500' : 'border-white/30 hover:border-green-500 bg-black/50'} flex items-center justify-center transition-colors z-10`}
                          >
                            {isSelected && <Check className="w-3 h-3 text-white" />}
                          </button>
                          
                          <div 
                            onClick={() => window.open(`https://app.hubspot.com/contacts/deals/${deal.id}`, '_blank')}
                            className="cursor-pointer pt-4"
                          >
                            {/* Deal Icon */}
                            <div className="w-14 h-14 bg-green-500/20 rounded-xl flex items-center justify-center mx-auto mb-3">
                              <Briefcase className="w-7 h-7 text-green-400" />
                            </div>
                            
                            {/* Deal Name */}
                            <h3 className="text-white font-medium text-center truncate group-hover:text-green-400 transition-colors">
                              {deal.dealname || 'Unnamed Deal'}
                            </h3>
                            
                            {/* Amount */}
                            {deal.amount && (
                              <p className="text-lg font-bold text-green-400 text-center mt-1">
                                {formatCurrency(deal.amount)}
                              </p>
                            )}
                            
                            {/* Stage Badge */}
                            <div className="mt-2 flex justify-center">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${getStageColor(deal.dealstage)}`}>
                                {formatStageName(deal.dealstage)}
                              </span>
                            </div>
                            
                            {/* Close Date */}
                            {deal.closedate && (
                              <p className="text-xs text-gray-500 text-center mt-2 flex items-center justify-center gap-1">
                                <Clock className="w-3 h-3" />
                                {formatDate(deal.closedate)}
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              ) : (
                /* List View for Deals */
                <div className="grid gap-3">
                  {filteredDeals.length === 0 ? (
                    <div className="text-center py-12 text-gray-400">
                      {dealStageFilter ? 'No deals match your filter.' : 'No deals found in your HubSpot account.'}
                    </div>
                  ) : (
                    filteredDeals.map((deal) => {
                      const isSelected = selectedDeals.has(deal.id);
                      return (
                        <div
                          key={deal.id}
                          className={`bg-[#111] border rounded-xl p-4 transition-all group ${isSelected ? 'border-green-500 bg-green-500/5' : 'border-white/10 hover:border-green-500/50'}`}
                          data-testid={`deal-${deal.id}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              {/* Checkbox */}
                              <button
                                onClick={(e) => { e.stopPropagation(); toggleDealSelection(deal.id); }}
                                className={`w-6 h-6 rounded border ${isSelected ? 'bg-green-500 border-green-500' : 'border-white/30 hover:border-green-500'} flex items-center justify-center transition-colors`}
                              >
                                {isSelected && <Check className="w-4 h-4 text-white" />}
                              </button>
                              
                              <div 
                                onClick={() => window.open(`https://app.hubspot.com/contacts/deals/${deal.id}`, '_blank')}
                                className="cursor-pointer"
                              >
                                <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center">
                                  <Briefcase className="w-6 h-6 text-green-400" />
                                </div>
                              </div>
                              <div 
                                onClick={() => window.open(`https://app.hubspot.com/contacts/deals/${deal.id}`, '_blank')}
                                className="cursor-pointer"
                              >
                                <h3 className="text-white font-medium group-hover:text-green-400 transition-colors">
                                  {deal.dealname || 'Unnamed Deal'}
                                </h3>
                                <div className="flex items-center gap-3 mt-1">
                                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${getStageColor(deal.dealstage)}`}>
                                    {formatStageName(deal.dealstage)}
                                  </span>
                                  {deal.closedate && (
                                    <span className="flex items-center gap-1 text-xs text-gray-500">
                                      <Clock className="w-3 h-3" />
                                      Close: {formatDate(deal.closedate)}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-4">
                              {deal.amount && (
                                <span className="text-xl font-bold text-green-400">
                                  {formatCurrency(deal.amount)}
                                </span>
                              )}
                              <ExternalLink 
                                onClick={() => window.open(`https://app.hubspot.com/contacts/deals/${deal.id}`, '_blank')}
                                className="w-5 h-5 text-gray-500 group-hover:text-green-400 transition-colors cursor-pointer" 
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Folders Tab */}
      {activeTab === 'folders' && (
        <div className="space-y-6">
          {/* Create Folder Button */}
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-white">My Folders ({folders.length})</h2>
            <Button
              onClick={() => setShowCreateFolder(true)}
              className="bg-orange-500 hover:bg-orange-600 text-white"
            >
              <FolderPlus className="w-4 h-4 mr-2" />
              Create Folder
            </Button>
          </div>

          {/* Create Folder Input */}
          {showCreateFolder && (
            <div className="bg-[#111] border border-white/10 rounded-xl p-4">
              <h3 className="text-white font-medium mb-3">Create New Folder</h3>
              <div className="flex gap-3">
                <Input
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  placeholder="Folder name..."
                  className="flex-1 bg-black border-white/20 text-white"
                  onKeyDown={(e) => e.key === 'Enter' && createFolder()}
                />
                <Button onClick={createFolder} className="bg-orange-500 hover:bg-orange-600">
                  Create
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => { setShowCreateFolder(false); setNewFolderName(''); }}
                  className="border-white/20 text-white hover:bg-white/10"
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {/* Folders List */}
          {folders.length === 0 ? (
            <div className="text-center py-20 border border-dashed border-white/20 rounded-2xl">
              <Folder className="w-16 h-16 text-gray-500 mx-auto mb-4" />
              <h3 className="text-xl font-medium text-white mb-2">No folders yet</h3>
              <p className="text-gray-400 mb-6">Create folders to organize your contacts and companies</p>
              <Button onClick={() => setShowCreateFolder(true)} className="bg-orange-500 hover:bg-orange-600">
                <FolderPlus className="w-4 h-4 mr-2" />
                Create Your First Folder
              </Button>
            </div>
          ) : (
            <div className="grid gap-4">
              {folders.map((folder) => (
                <div
                  key={folder.id}
                  className="bg-[#111] border border-white/10 rounded-xl overflow-hidden hover:border-orange-500/30 transition-all"
                >
                  {/* Folder Header */}
                  <div className="p-4 border-b border-white/10 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-orange-500/20 rounded-xl flex items-center justify-center">
                        <Folder className="w-5 h-5 text-orange-400" />
                      </div>
                      <div>
                        <h3 className="text-white font-medium">{folder.name}</h3>
                        <p className="text-xs text-gray-500">
                          {folder.contacts?.length || 0} contacts • {folder.companies?.length || 0} companies
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        if (confirm(`Delete folder "${folder.name}"?`)) {
                          deleteFolder(folder.id);
                        }
                      }}
                      className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>

                  {/* Folder Contents */}
                  <div className="p-4">
                    {/* Contacts in Folder */}
                    {folder.contacts?.length > 0 && (
                      <div className="mb-4">
                        <h4 className="text-sm font-medium text-gray-400 mb-2 flex items-center gap-2">
                          <Users className="w-4 h-4" />
                          Contacts ({folder.contacts.length})
                        </h4>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                          {folder.contacts.slice(0, 8).map((contactId) => {
                            const contact = contacts.find(c => c.id === contactId);
                            if (!contact) return null;
                            return (
                              <div
                                key={contactId}
                                onClick={() => setSelectedContact(contact)}
                                className="flex items-center gap-2 p-2 bg-white/5 rounded-lg cursor-pointer hover:bg-white/10 transition-colors"
                              >
                                <div className="w-8 h-8 rounded-full bg-orange-500/20 flex items-center justify-center text-orange-400 text-xs font-medium">
                                  {(contact.firstname?.[0] || contact.email?.[0] || '?').toUpperCase()}
                                </div>
                                <div className="min-w-0 flex-1">
                                  <p className="text-sm text-white truncate">
                                    {contact.firstname || contact.lastname 
                                      ? `${contact.firstname || ''} ${contact.lastname || ''}`.trim()
                                      : contact.email || 'Unknown'}
                                  </p>
                                </div>
                              </div>
                            );
                          })}
                          {folder.contacts.length > 8 && (
                            <div className="flex items-center justify-center p-2 bg-white/5 rounded-lg text-gray-400 text-sm">
                              +{folder.contacts.length - 8} more
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Companies in Folder */}
                    {folder.companies?.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-400 mb-2 flex items-center gap-2">
                          <Building2 className="w-4 h-4" />
                          Companies ({folder.companies.length})
                        </h4>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                          {folder.companies.slice(0, 8).map((companyId) => {
                            const company = companies.find(c => c.id === companyId);
                            if (!company) return null;
                            return (
                              <div
                                key={companyId}
                                onClick={() => window.open(`https://app.hubspot.com/contacts/companies/${companyId}`, '_blank')}
                                className="flex items-center gap-2 p-2 bg-white/5 rounded-lg cursor-pointer hover:bg-white/10 transition-colors"
                              >
                                {company.logo_url ? (
                                  <img src={company.logo_url} alt="" className="w-8 h-8 rounded object-contain bg-white p-0.5" />
                                ) : (
                                  <div className="w-8 h-8 rounded bg-purple-500/20 flex items-center justify-center">
                                    <Building2 className="w-4 h-4 text-purple-400" />
                                  </div>
                                )}
                                <div className="min-w-0 flex-1">
                                  <p className="text-sm text-white truncate">{company.name || 'Unknown'}</p>
                                </div>
                              </div>
                            );
                          })}
                          {folder.companies.length > 8 && (
                            <div className="flex items-center justify-center p-2 bg-white/5 rounded-lg text-gray-400 text-sm">
                              +{folder.companies.length - 8} more
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Empty folder message */}
                    {(!folder.contacts?.length && !folder.companies?.length) && (
                      <div className="text-center py-6 text-gray-500">
                        <p>This folder is empty</p>
                        <p className="text-xs mt-1">Select contacts or companies and use &quot;Add to Folder&quot;</p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Modals */}
      {showCreateContact && <CreateContactModal />}
      {showFolderModal && <FolderModal />}
      {selectedContact && (
        <ContactDetailModal 
          contact={selectedContact} 
          onClose={() => setSelectedContact(null)} 
        />
      )}
    </div>
  );
}
